import { EventTracker, eventsDisabled } from './tracker';
import { TrackerOptions } from './types';

/**
 * Process-wide tracker.
 *
 * A singleton because the durable buffer is a single IndexedDB store and the
 * flush loop must be single-flight: two trackers would read the same rows and
 * send them twice. Harmless server-side (events dedupe on eventId) but it
 * wastes the user's bandwidth and corrupts the duplicate-rate metric that tells
 * us whether clients are retrying.
 */
let tracker: EventTracker | null = null;

/**
 * Configure and start the tracker. Call once during app bootstrap if you want
 * non-default options; otherwise the first `getEventTracker()` starts one with
 * the defaults below.
 */
export function initEventTracker(options: TrackerOptions): EventTracker {
  if (tracker) return tracker;
  tracker = new EventTracker(options);
  tracker.start();
  return tracker;
}

export function getEventTracker(): EventTracker {
  return tracker ?? initEventTracker({ source: 'lms-web' });
}

/**
 * Fire-and-forget helper for call sites on the interaction path.
 *
 * Tracking must never break, block or slow down the thing being tracked, so
 * this swallows failures rather than surfacing them: a broken analytics call
 * that takes a lesson down with it is far worse than a missing event. Real
 * delivery failures are retried by the tracker and surface as buffer growth
 * and the server-side metrics in §10.4, not here.
 */
export function trackEvent(
  eventType: string,
  payload: unknown,
  correlation?: Record<string, unknown>,
  /** See EventTracker.track — only for events that did not happen "now". */
  occurredAt?: string,
): void {
  // Disabled: do not even create the tracker.
  if (eventsDisabled()) return;

  try {
    void getEventTracker()
      .track(eventType, payload, correlation, occurredAt)
      .catch(() => undefined);
  } catch {
    /* never throw from a tracking call */
  }
}
